import React from 'react';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { Calendar } from 'lucide-react';
import ThemeSelect from './ThemeSelect';

const { RangePicker } = DatePicker;

/**
 * Calculates start and end dates formatted as YYYY-MM-DD for standard presets.
 */
export const resolveDatePreset = (preset, customStart = '', customEnd = '') => {
  const now = new Date();
  const fmt = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const fmtS = (d) => { d.setHours(0, 0, 0, 0); return fmt(d); };
  const fmtE = (d) => { d.setHours(23, 59, 59, 999); return fmt(d); };

  switch (preset) {
    case 'today':
      return { startDate: fmtS(new Date(now)), endDate: fmtE(new Date(now)) };
    case 'yesterday': {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      return { startDate: fmtS(y), endDate: fmtE(new Date(y)) };
    }
    case 'this_week': {
      const d = now.getDay();
      const diff = now.getDate() - d + (d === 0 ? -6 : 1);
      const s = new Date(now);
      s.setDate(diff);
      return { startDate: fmtS(s), endDate: fmtE(new Date(now)) };
    }
    case 'last_week': {
      const s = new Date(now);
      s.setDate(s.getDate() - 7);
      return { startDate: fmtS(s), endDate: fmtE(new Date(now)) };
    }
    case 'this_month':
      return {
        startDate: fmtS(new Date(now.getFullYear(), now.getMonth(), 1)),
        endDate: fmtE(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
      };
    case 'last_month':
      return {
        startDate: fmtS(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
        endDate: fmtE(new Date(now.getFullYear(), now.getMonth(), 0)),
      };
    case 'this_year':
      return {
        startDate: fmtS(new Date(now.getFullYear(), 0, 1)),
        endDate: fmtE(new Date(now.getFullYear(), 11, 31)),
      };
    case 'custom':
      return { startDate: customStart || '', endDate: customEnd || '' };
    default:
      return { startDate: '', endDate: '' };
  }
};

export const DEFAULT_DATE_PRESETS = [
  { label: 'All Time', value: 'all_time' },
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'This Week', value: 'this_week' },
  { label: 'Last Week', value: 'last_week' },
  { label: 'This Month', value: 'this_month' },
  { label: 'Last Month', value: 'last_month' },
  { label: 'This Year', value: 'this_year' },
  { label: 'Custom Range', value: 'custom' },
];

/**
 * Reusable Date Range Filter Component
 * 
 * Props:
 * - preset: string ('today', 'this_week', 'custom', 'all_time', '')
 * - startDate: string ('YYYY-MM-DD')
 * - endDate: string ('YYYY-MM-DD')
 * - onChange: function({ preset, startDate, endDate })
 * - showPresets: boolean (default: true)
 * - label: string (optional, default: 'Date Range')
 * - placeholder: string (optional, default: 'Select Date')
 * - presetOptions: array (optional)
 * - customTriggerValue: string (value that triggers custom range picker, default: 'custom')
 * - style: object
 * - className: string
 */
const DateRangeFilter = ({
  preset = '',
  startDate = '',
  endDate = '',
  onChange,
  showPresets = true,
  label = 'Date Range',
  placeholder = 'Select Date',
  presetOptions = DEFAULT_DATE_PRESETS,
  customTriggerValue = 'custom',
  style = {},
  className = 'w-full',
  size = 'middle',
}) => {
  const handlePresetChange = (newPreset) => {
    if (!onChange) return;
    if (newPreset === customTriggerValue) {
      onChange({
        preset: newPreset,
        startDate: startDate || '',
        endDate: endDate || '',
      });
    } else {
      const { startDate: resolvedStart, endDate: resolvedEnd } = resolveDatePreset(newPreset);
      onChange({
        preset: newPreset,
        startDate: resolvedStart,
        endDate: resolvedEnd,
      });
    }
  };

  const handleRangeChange = (dates) => {
    if (!onChange) return;
    if (dates && dates[0] && dates[1]) {
      onChange({
        preset: customTriggerValue,
        startDate: dates[0].format('YYYY-MM-DD'),
        endDate: dates[1].format('YYYY-MM-DD'),
      });
    } else {
      onChange({
        preset: customTriggerValue,
        startDate: '',
        endDate: '',
      });
    }
  };

  const pickerValue = startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : null;

  if (!showPresets) {
    return (
      <div className={className} style={style}>
        {label && <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>}
        <RangePicker
          value={pickerValue}
          onChange={handleRangeChange}
          format="DD-MM-YYYY"
          popupClassName="!z-[100005]"
          style={{ width: '100%', borderRadius: '8px', padding: '6px 12px' }}
          size={size}
        />
      </div>
    );
  }

  const isCustomActive = preset === customTriggerValue;

  return (
    <div className={className} style={style}>
      {label && <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>}
      <ThemeSelect
        value={preset}
        onChange={handlePresetChange}
        options={presetOptions}
        icon={Calendar}
        className="w-full"
        placeholder={placeholder}
      />
      {isCustomActive && (
        <div className="mt-2">
          <RangePicker
            value={pickerValue}
            onChange={handleRangeChange}
            format="DD-MM-YYYY"
            popupClassName="!z-[100005]"
            style={{ width: '100%', borderRadius: '8px', padding: '6px 12px' }}
            size={size}
          />
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
